import { Controller } from "@hotwired/stimulus";

export class VisibilityToggleController extends Controller {
  static targets = ["visibilityTrigger", "hide"];
  static values = {
    showWhenValue: String,
    hideWhenValue: String,
    radioGroupName: String
  };

  connect() {
    this.setupEventListeners();
    this.setVisibility();
  }

  setupEventListeners() {
    const radioGroupName = this.getRadioGroupName();
    
    if (radioGroupName) {
      // Bind to all radio buttons in the group
      const radioButtons = this.element.querySelectorAll(`input[type="radio"][name="${radioGroupName}"]`);
      radioButtons.forEach((radio) => {
        radio.addEventListener("change", () => this.setVisibility());
      });
    } else if (this.hasVisibilityTriggerTarget) {
      // For checkboxes, the change event is handled via data-action attribute
      // For radio buttons with explicit trigger, bind to all radios in the group
      const trigger = this.visibilityTriggerTarget;
      if (trigger.type === "radio") {
        const radioButtons = this.element.querySelectorAll(`input[type="radio"][name="${trigger.name}"]`);
        radioButtons.forEach((radio) => {
          radio.addEventListener("change", () => this.setVisibility());
        });
      }
    }
  }

  getRadioGroupName() {
    if (this.hasRadioGroupNameValue) {
      return this.radioGroupNameValue;
    }
    return null;
  }

  getSelectedRadioValue() {
    const radioGroupName = this.getRadioGroupName();
    
    if (radioGroupName) {
      const selectedRadio = this.element.querySelector(`input[type="radio"][name="${radioGroupName}"]:checked`);
      return selectedRadio?.value || null;
    }
    
    if (this.hasVisibilityTriggerTarget && this.visibilityTriggerTarget.type === "radio") {
      const name = this.visibilityTriggerTarget.name;
      const selectedRadio = this.element.querySelector(`input[type="radio"][name="${name}"]:checked`);
      return selectedRadio?.value || null;
    }
    
    return null;
  }

  evaluateRadioValue(selectedValue) {
    if (!selectedValue) {
      return false;
    }
    
    // If hideWhenValue is set, hide when that value is selected
    if (this.hasHideWhenValueValue && selectedValue === this.hideWhenValueValue) {
      return false;
    }
    
    // If showWhenValue is set, show only when that value is selected
    if (this.hasShowWhenValueValue) {
      return selectedValue === this.showWhenValueValue;
    }
    
    // Default: show if any radio button is selected (except if hideWhenValue matches)
    return true;
  }

  shouldShow() {
    // Handle radio button groups
    const radioValue = this.getSelectedRadioValue();
    if (radioValue !== null) {
      return this.evaluateRadioValue(radioValue);
    }
    
    // Handle checkboxes (original behavior)
    if (this.hasVisibilityTriggerTarget) {
      return this.visibilityTriggerTarget.checked;
    }
    
    return false;
  }

  setVisibility() {
    const shouldShow = this.shouldShow();

    this.hideTargets.forEach((el) => {
      // Find the form field wrapper to hide the entire field including label
      const wrapper = el.closest(".FormControl-check-group-wrap") ||
                      el.closest(".FormControl") ||
                      el.parentElement;

      if (shouldShow) {
        el.hidden = false;
        el.removeAttribute("hidden");
        if (wrapper && wrapper !== el && wrapper !== this.element) {
          wrapper.style.removeProperty("display");
        }
      } else {
        el.hidden = true;
        if (!el.hasAttribute("hidden")) {
          el.setAttribute("hidden", "hidden");
        }
        if (wrapper && wrapper !== el && wrapper !== this.element) {
          wrapper.style.display = "none";
        }
      }
    });
  }
}